import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitiesSearchAutocomplete, {
  BaseEntitiesSearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitiesSearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { AutocompleteOptionType } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { DamageDetectionDto, DamageDetectionGetPaginatedDto } from "@/core/api/generated/v1";

import ContractInline from "../Contract/ContractInline";
import VehicleInline from "../Vehicle/VehicleInline";
import { damageDetectionToOption } from "./DamageDetectionAutocomplete";

export interface DamageDetectionsAutocompleteProps extends BaseEntitiesSearchAutocompleteInheritableProps<DamageDetectionDto> {
  searchFilters?: Partial<Omit<DamageDetectionGetPaginatedDto, "search" | "includeIds">>;
}

export default function DamageDetectionsAutocomplete({
  isPreload = true,
  searchFilters,
  ...otherProps
}: DamageDetectionsAutocompleteProps) {
  return (
    <BaseEntitiesSearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={damageDetectionToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.damageDetectionsApi.apiV1DamageDetectionsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          damageDetectionGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          damageDetectionGetPaginatedDto: {
            ...params.damageDetectionGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Damage detections'
      placeholder='Search for damage detection...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='damageDetection' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography component='div' variant='body1'>
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      <Box>
                        <VehicleInline
                          entity={option.data.vehicle}
                          withIcon={false}
                          withTooltip={false}
                        />
                      </Box>
                      {option.data?.contract && (
                        <Box>
                          <ContractInline
                            entity={option.data?.contract}
                            withIcon={false}
                            withTooltip={false}
                          />
                        </Box>
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
